/**
 * Theme tokens → literal colours.
 *
 * The point of drawing the life map as one `<svg>` is that the user can take
 * it with them — save it, print it, drop it in a doc. A file full of
 * `var(--brand-primary)` and `color-mix(…)` renders as black-on-nothing the
 * moment it leaves the app, so every colour is resolved here to an `rgb()`
 * string before it reaches the markup.
 *
 * Resolution goes through a throwaway element rather than
 * `getPropertyValue()`, which hands back the raw declaration text and so does
 * not evaluate `color-mix`. Setting `color` and reading it back makes the
 * browser do the arithmetic for us, including whichever theme is active.
 */

export interface TypePalette {
  stroke: string
  fill: string
  /** Slightly lifted fill for the gradient's top stop. */
  fillTop: string
  accent: string
}

export interface Palette {
  fontFamily: string
  text: string
  textMuted: string
  surface: string
  surfaceAlt: string
  borderSoft: string
  brand: string
  brandSoft: string
  warning: string
  error: string
  info: string
  types: Record<string, TypePalette>
  fallbackType: TypePalette
}

let probe: HTMLElement | null = null

/** Evaluate any CSS colour expression against the live theme. */
function resolveColor(expr: string, fallback: string): string {
  if (typeof document === 'undefined') return fallback
  if (!probe) {
    probe = document.createElement('span')
    probe.style.position = 'absolute'
    probe.style.visibility = 'hidden'
    probe.style.pointerEvents = 'none'
    document.body.appendChild(probe)
  }
  probe.style.color = ''
  probe.style.color = expr
  const got = getComputedStyle(probe).color
  // An unsupported expression leaves `color` unset, which computes to the
  // inherited value — indistinguishable from a real answer, so treat an empty
  // or transparent result as a miss.
  if (!got || got === 'rgba(0, 0, 0, 0)') return fallback
  return got
}

const mix = (a: string, pct: number, b: string) =>
  `color-mix(in srgb, var(${a}) ${pct}%, var(${b}))`

/** The node palette, keyed by entity type — one purple ramp by depth. */
function typePalettes(): Record<string, TypePalette> {
  const entry = (
    strokeVar: string,
    strokeFallback: string,
    fillPct: number,
  ): TypePalette => ({
    stroke: resolveColor(`var(${strokeVar})`, strokeFallback),
    fill: resolveColor(mix(strokeVar, fillPct, '--bg-surface'), '#ffffff'),
    fillTop: resolveColor(mix(strokeVar, Math.round(fillPct * 1.7), '--bg-surface'), '#ffffff'),
    accent: resolveColor(`var(${strokeVar})`, strokeFallback),
  })

  return {
    dream: entry('--brand-dark', '#4E2F78', 11),
    aspiration: entry('--brand-dark-mid', '#5F3D92', 10),
    goal: entry('--brand-primary', '#7851A9', 10),
    skill: entry('--brand-light', '#9575C4', 11),
    habit: entry('--brand-light', '#9575C4', 8),
    task: entry('--brand-lighter', '#C9B8E8', 16),
    action_item: entry('--brand-lighter', '#C9B8E8', 12),
    roadblock: entry('--semantic-error', '#B75A4A', 12),
    // People and places are a different axis of the map — who and where —
    // so they step off the purple ramp on purpose.
    person: entry('--border-strong', '#CFC6DD', 14),
    place: entry('--semantic-info', '#4A90A4', 12),
  }
}

/**
 * Read the palette for whatever theme is currently applied to `<html>`.
 * Cheap enough to call on every theme change; there is no cache because a
 * stale palette is a visibly wrong diagram.
 */
export function readPalette(): Palette {
  const bodyFont =
    typeof document !== 'undefined'
      ? getComputedStyle(document.body).fontFamily
      : ''

  return {
    fontFamily:
      bodyFont ||
      '-apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
    text: resolveColor('var(--text-primary)', '#1A1A1A'),
    textMuted: resolveColor('var(--text-secondary)', '#5B626C'),
    surface: resolveColor('var(--bg-surface)', '#FBFAFD'),
    surfaceAlt: resolveColor('var(--bg-surface-alt)', '#EDE9F4'),
    borderSoft: resolveColor('var(--border-soft)', '#DDD8E6'),
    brand: resolveColor('var(--brand-primary)', '#7851A9'),
    brandSoft: resolveColor(mix('--brand-primary', 12, '--bg-surface'), '#F1ECF8'),
    warning: resolveColor('var(--semantic-warning)', '#D97706'),
    error: resolveColor('var(--semantic-error)', '#B75A4A'),
    info: resolveColor('var(--semantic-info)', '#4A90A4'),
    types: typePalettes(),
    fallbackType: {
      stroke: resolveColor('var(--border-strong)', '#CFC6DD'),
      fill: resolveColor('var(--bg-surface)', '#FBFAFD'),
      fillTop: resolveColor('var(--bg-surface)', '#FBFAFD'),
      accent: resolveColor('var(--text-secondary)', '#5B626C'),
    },
  }
}

export const paletteFor = (p: Palette, type: string): TypePalette =>
  p.types[type] ?? p.fallbackType

/** Stroke treatment for a cross-link, by relationship. */
export function crossStroke(
  p: Palette,
  relationship: string,
): { stroke: string; dash: string; width: number } {
  switch (relationship) {
    case 'blocks':
    case 'blocked_by':
      return { stroke: p.error, dash: '7 4', width: 1.7 }
    case 'derived_from':
      return { stroke: p.types.task.stroke, dash: '2 4', width: 1.4 }
    case 'location':
      return { stroke: p.info, dash: '1 4', width: 1.5 }
    case 'planned':
      return { stroke: p.brand, dash: '5 4', width: 1.4 }
    default:
      return { stroke: p.textMuted, dash: '5 4', width: 1.3 }
  }
}
